/**
 * MarketplaceHeroSearch
 * Alibaba-style full-width hero search section placed in the PAGE BODY
 * (not in the header). Contains:
 *   — Big mode tabs: AI Mode | Products | Manufacturers | Worldwide
 *   — Large search bar: [📷 Image Search | input | 🔍 Search]
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, SOCKET_URL } from '../../config/api';
import './MarketplaceHeroSearch.css';

const TABS = [
  { id: 'Products',      label: 'Products',      sup: null },
  { id: 'AI Mode',       label: 'AI Mode',        sup: '+' },
  { id: 'Manufacturers', label: 'Manufacturers',  sup: null },
  { id: 'Worldwide',     label: 'Worldwide',      sup: null },
];

export default function MarketplaceHeroSearch() {
  const navigate = useNavigate();

  const [activeTab,  setActiveTab]  = useState('Products');
  const [query,      setQuery]      = useState('');
  const [prodSugs,   setProdSugs]   = useState([]);
  const [suppSugs,   setSuppSugs]   = useState([]);
  const [showDD,     setShowDD]     = useState(false);

  const wrapRef   = useRef(null);
  const inputRef  = useRef(null);
  const timerRef  = useRef(null);
  const fileRef   = useRef(null);

  /* ── outside click closes dropdown ── */
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDD(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── navigate on tab click ── */
  const onTabClick = (id) => {
    setActiveTab(id);
    if (id === 'AI Mode')       navigate('/mobile/ai-mode');
    else if (id === 'Manufacturers') navigate('/top-seller');
    else if (id === 'Worldwide')     navigate('/mobile/worldwide');
  };

  /* ── search ── */
  const doSearch = () => {
    const q = query.trim();
    if (!q) return;
    setShowDD(false);
    navigate(`/products?search=${encodeURIComponent(q)}`);
  };

  /* ── autocomplete ── */
  const fetchSugs = useCallback((term) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!term || term.trim().length < 2) {
      setProdSugs([]);
      setSuppSugs([]);
      setShowDD(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/products/search-suggestions?q=${encodeURIComponent(term.trim())}`
        );
        setProdSugs(res.data.suggestions || []);
        setSuppSugs(res.data.suppliers   || []);
        setShowDD(true);
      } catch {
        setProdSugs([]);
        setSuppSugs([]);
      }
    }, 300);
  }, []);

  const onSugClick = (s) => {
    setShowDD(false);
    setQuery('');
    if (s.categorySlug && s.subcategorySlug) navigate(`/category/${s.categorySlug}/${s.subcategorySlug}`);
    else if (s.categorySlug) navigate(`/category/${s.categorySlug}`);
    else navigate(`/product/${s.id}`);
  };

  /* ── image search (filename → query) ── */
  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ');
    setQuery(name);
    e.target.value = '';
    inputRef.current?.focus();
  };

  return (
    <section className="mhs-root">
      <div className="mhs-inner">

        {/* ── Big mode tabs ── */}
        <div className="mhs-tabs-row">
          {TABS.map((tab, i) => (
            <React.Fragment key={tab.id}>
              {i > 0 && <span className="mhs-tab-pipe">|</span>}
              <button
                type="button"
                className={`mhs-tab ${activeTab === tab.id ? 'mhs-tab--active' : ''}`}
                onClick={() => onTabClick(tab.id)}
              >
                {tab.label}
                {tab.sup && <sup className="mhs-tab-sup">{tab.sup}</sup>}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* ── Search bar ── */}
        <div className="mhs-bar-wrap" ref={wrapRef}>
          <div className="mhs-bar">

            {/* Image search button */}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={onFileChange}
            />
            <button
              type="button"
              className="mhs-img-btn"
              onClick={() => fileRef.current?.click()}
              title="Search by image"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
              <span className="mhs-img-label">Image Search</span>
            </button>

            {/* Text input */}
            <input
              ref={inputRef}
              type="search"
              className="mhs-input"
              value={query}
              onChange={e => { setQuery(e.target.value); fetchSugs(e.target.value); }}
              onKeyDown={e => e.key === 'Enter' && doSearch()}
              placeholder="What are you looking for?"
              autoComplete="off"
            />

            {/* Search button */}
            <button type="button" className="mhs-search-btn" onClick={doSearch}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <span>Search</span>
            </button>
          </div>

          {/* ── Autocomplete dropdown ── */}
          {showDD && (prodSugs.length > 0 || suppSugs.length > 0) && (
            <div className="mhs-dropdown">
              {suppSugs.length > 0 && (
                <div>
                  <p className="mhs-dd-label">Suppliers</p>
                  {suppSugs.map(item => (
                    <button key={item.id} type="button" className="mhs-dd-item"
                      onClick={() => navigate(`/seller/${item.id}`)}
                    >
                      <span className="mhs-dd-name">{item.name}</span>
                    </button>
                  ))}
                </div>
              )}
              {prodSugs.length > 0 && (
                <div className={suppSugs.length > 0 ? 'mhs-dd-sep' : ''}>
                  <p className="mhs-dd-label">Products</p>
                  {prodSugs.map(item => (
                    <button key={item.id} type="button" className="mhs-dd-item"
                      onClick={() => onSugClick(item)}
                    >
                      {item.image && (
                        <img
                          src={item.image.startsWith('http') ? item.image : `${SOCKET_URL}${item.image}`}
                          alt=""
                          className="mhs-dd-thumb"
                        />
                      )}
                      <div>
                        <div className="mhs-dd-name">{item.name}</div>
                        <div className="mhs-dd-cat">
                          {item.categoryName}{item.subcategoryName ? ` › ${item.subcategoryName}` : ''}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <button type="button" className="mhs-dd-all" onClick={doSearch}>
                View all results for "{query}"
              </button>
            </div>
          )}
        </div>

      </div>
    </section>
  );
}
